import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

// Loaded at runtime by the CDN tag in layouts/gala.slim, the same way the
// conference registration form gets it.
const ITI_UTILS_URL =
  "https://cdn.jsdelivr.net/npm/intl-tel-input@25.10.10/build/js/utils.js";

/** Gap between trigger and menu, and the breathing room kept at the viewport
 *  edge. MENU_MIN_HEIGHT is the point below which dropping down stops being
 *  worth it and the menu flips above the trigger instead. */
const MENU_GAP = 8;
const MENU_GUTTER = 16;
const MENU_MIN_HEIGHT = 200;

interface Placement {
  direction: "down" | "up";
  maxHeight: number;
}

interface MainCourse {
  id: number;
  name: string;
  description: string | null;
}

/** Admin-replaceable invitation artwork, with the bundled file as fallback.
 *  `alt` is built server-side and deliberately generic: the artwork carries the
 *  date and venue, and it can be reworded from /admin without a deploy. */
interface InviteSources {
  desktop: string;
  mobile: string;
  alt: string;
}

interface RegistrationProps {
  token: string;
  url: string;
  mainCourses: MainCourse[];
  open: boolean;
  /** This guest has already submitted. Outranks `open`: their answer is in,
   *  whatever the guest list is doing now. */
  reserved: boolean;
  /** null whenever `open` is false, and only then. */
  invitee: Invitee | null;
  invite: InviteSources;
  /** The airline's own mark, drawn above the invitation. Bundled rather than
   *  admin-replaceable: unlike the lockup below it, it does not change. */
  logo: string;
  /** null when Turnstile is not configured for this environment. */
  turnstileSiteKey: string | null;
}

/**
 * The two end states, both drawn as the same card in the slot the form
 * occupies. Split by script rather than kept flat, because the frames set the
 * two differently: the Chinese lines are the lighter, smaller pair and the
 * English ones carry the weight.
 */
interface StatusCopy {
  headings: { zh: string; en: string };
  body: { zh: string[]; en: string[] };
}

const SUCCESS_COPY: StatusCopy = {
  headings: { zh: "报名成功", en: "Selection confirmed" },
  body: {
    zh: [
      "确认邮件将发送至您登记的邮箱。",
      "我们期待在马来西亚国际航空2026年度璀璨之夜晚宴上与您见面。",
    ],
    en: [
      "Thank you for completing your main course selection.", 
      "A confirmation email with your event details will be sent to you shortly.",
      "",
      "We look forward to welcoming you to the Malaysia Airlines Gala Dinner 2026.",
    ],
  },
};

const CLOSED_COPY: StatusCopy = {
  headings: { zh: "报名已截止", en: "Registration is now closed" },
  body: {
    zh: ["感谢您的关注与支持。"],
    en: ["Thank you for your interest and participation."],
  },
};

/** The four plain text fields, in the order the design lists them. */
const TEXT_FIELDS = [
  {
    name: "full_name",
    label: "Name / 姓名",
    type: "text",
    autoComplete: "name",
  },
  {
    name: "company_name",
    label: "Company name / 公司",
    type: "text",
    autoComplete: "organization",
  },
  {
    name: "designation",
    label: "Designation / 职位",
    type: "text",
    autoComplete: "organization-title",
  },
  {
    name: "email",
    label: "Email / 邮箱",
    type: "email",
    autoComplete: "email",
  },
] as const;

/** Every field the form reports an error against. */
type FieldName =
  | (typeof TEXT_FIELDS)[number]["name"]
  | "phone"
  | "main_course_id";

/** Phone is absent on purpose -- see the note on phoneRef. */
type ControlledField =
  | (typeof TEXT_FIELDS)[number]["name"]
  | "main_course_id";

const EMPTY_FORM: Record<ControlledField, string> = {
  full_name: "",
  company_name: "",
  designation: "",
  email: "",
  main_course_id: "",
};

/** The row an organiser created for this guest, as the link named it. Its
 *  controlled fields are keyed exactly as the form names its inputs -- and as
 *  the server keys its errors -- so they drop straight into form state. */
type Invitee = Record<ControlledField, string> & {
  /** Which row the submission completes. Posted back untouched; never shown. */
  identityLabel: string;
  /** Stored E.164, or "". Goes to the country picker, not to form state. */
  phone: string;
  /** Stored alpha-2, or "". The picker's starting country when phone is "". */
  country: string;
};

/** Driven off EMPTY_FORM's keys rather than a second list of field names, so a
 *  field added to the form cannot be left out of the prefill. */
const formFrom = (invitee: Invitee | null): Record<ControlledField, string> => {
  if (!invitee) return EMPTY_FORM;

  const filled = { ...EMPTY_FORM };
  (Object.keys(EMPTY_FORM) as ControlledField[]).forEach((key) => {
    filled[key] = invitee[key] ?? "";
  });
  return filled;
};

const Label: React.FC<{
  htmlFor: string;
  id?: string;
  children: React.ReactNode;
}> = ({ htmlFor, id, children }) => (
  <label
    htmlFor={htmlFor}
    id={id}
    className="block text-sm leading-5 text-gala-gold"
  >
    {children}
  </label>
);

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? (
    <p className="mt-1 text-xs font-medium text-red-200">{message}</p>
  ) : null;

/** Headings are uppercased in CSS, which the Chinese lines pass through
 *  unchanged -- so both scripts can be written naturally here. */
const StatusCard: React.FC<{ copy: StatusCopy }> = ({ copy }) => (
  <div className="flex w-full flex-col items-center gap-5 overflow-hidden rounded-lg bg-white px-6 py-8 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)]">
    <div
      className="flex size-13 shrink-0 items-center justify-center rounded-full bg-blue-100 xl:size-20"
      aria-hidden="true"
    >
      {/* Heavier than Heroicons draws it by default: the frames set the tick at
          4 units on a 40 box. */}
      <CheckIcon
        className="size-[30px] text-gala-ink xl:size-10"
        strokeWidth={2.4}
      />
    </div>

    <div className="flex w-full flex-col items-center gap-6 text-center">
      {/* Capped at the width the frames wrap the headings to -- it is what
          breaks REGISTRATION IS NOW CLOSED over two lines. */}
      <div className="flex max-w-[301px] flex-col gap-2 text-2xl uppercase leading-normal text-gala-ink xl:text-[28px]">
        <h2 className="font-normal">{copy.headings.zh}</h2>
        <h2 className="font-bold">{copy.headings.en}</h2>
      </div>

      <div className="flex w-full flex-col gap-2 text-gray-500 opacity-80">
        <div className="text-[13px] leading-4">
          {copy.body.zh.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <div className="text-[15px] leading-normal">
          {copy.body.en.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
    </div>
  </div>
);


/**
 * The Main Course field. A native <select> cannot show a description under each
 * dish, which both Figma frames do -- closed, the trigger carries the chosen
 * dish's name and description; open, the menu lists every dish the same way.
 * Built as a listbox so the keyboard still works: Enter/Space/arrows open it,
 * arrows move, Enter picks, Escape closes.
 */
const MainCourseSelect: React.FC<{
  courses: MainCourse[];
  selectedId: string;
  onSelect: (id: string) => void;
}> = ({ courses, selectedId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<Placement>({
    direction: "down",
    maxHeight: MENU_MIN_HEIGHT,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = courses.find((c) => String(c.id) === selectedId) ?? null;
  // The menu is admin-managed and seeded by a rake task that is deliberately not
  // part of db:seed, so an environment can legitimately reach this page with no
  // dishes. Say so, rather than opening an empty box the guest cannot satisfy.
  const isEmpty = courses.length === 0;

  // Fit the menu to the space that actually exists: drop it below the trigger
  // when there is room, flip it above when there is not, and cap its height to
  // whichever gap it lands in. Without this it runs past the bottom of the
  // document and leaves the page scrollable into empty space.
  const fitToViewport = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const below = window.innerHeight - rect.bottom - MENU_GAP - MENU_GUTTER;
    const above = rect.top - MENU_GAP - MENU_GUTTER;
    const direction = below < MENU_MIN_HEIGHT && above > below ? "up" : "down";

    setPlacement({
      direction,
      maxHeight: Math.max(MENU_MIN_HEIGHT, Math.floor(direction === "down" ? below : above)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    // Scrolling or resizing moves the trigger, so the fit is recomputed rather
    // than measured once on open.
    window.addEventListener("resize", fitToViewport);
    window.addEventListener("scroll", fitToViewport, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("resize", fitToViewport);
      window.removeEventListener("scroll", fitToViewport, true);
    };
  }, [open, fitToViewport]);

  // Keep the keyboard-focused dish visible now that the menu can scroll.
  useEffect(() => {
    if (!open) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const choose = (course: MainCourse) => {
    onSelect(String(course.id));
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        if (isEmpty) return;
        setActiveIndex(selected ? courses.indexOf(selected) : 0);
        fitToViewport();
        setOpen(true);
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      // Guarded: the modulo below is a division by zero when the menu has not
      // been seeded, which sets activeIndex to NaN.
      if (isEmpty) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((i) => (i + step + courses.length) % courses.length);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const course = courses[activeIndex];
      if (course) choose(course);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        id="main_course_id"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isEmpty}
        onClick={() => {
          if (isEmpty) return;
          setActiveIndex(selected ? courses.indexOf(selected) : 0);
          fitToViewport();
          setOpen((v) => !v);
        }}
        onKeyDown={onKeyDown}
        className="gala-dropdown-trigger disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isEmpty ? (
          <span className="flex-1 text-left text-sm text-gray-400">
            The menu has not been published yet
          </span>
        ) : selected ? (
          <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
            <span className="text-sm leading-5 text-gray-800">
              {selected.name}
            </span>
            {selected.description && (
              <span className="whitespace-pre-line pl-[11px] text-xs font-light leading-4 text-gray-600">
                {selected.description}
              </span>
            )}
          </span>
        ) : (
          <span className="flex-1 text-left text-sm text-gray-400">Options</span>
        )}
        <ChevronDownIcon
          className={`size-5 shrink-0 text-gray-700 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && !isEmpty && (
        <ul
          ref={listRef}
          role="listbox"
          aria-labelledby="main_course_label"
          style={{ maxHeight: placement.maxHeight }}
          className={`gala-dropdown-menu absolute inset-x-0 z-20 ${
            placement.direction === "down" ? "top-full mt-2" : "bottom-full mb-2"
          }`}
        >
          {courses.map((course, index) => (
            <li
              key={course.id}
              role="option"
              aria-selected={String(course.id) === selectedId}
              onClick={() => choose(course)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`gala-dropdown-item ${index === activeIndex ? "is-active" : ""}`}
            >
              <span className="text-sm leading-5 text-gray-800">
                {course.name}
              </span>
              {course.description && (
                <span className="whitespace-pre-line pl-[11px] text-xs font-light leading-4 text-gray-600">
                  {course.description}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Registration: React.FC<RegistrationProps> = ({
  token,
  url,
  mainCourses,
  open,
  reserved,
  invitee,
  invite,
  logo,
  turnstileSiteKey,
}) => {
  // The organisers' answers are the form's starting point; from the first
  // keystroke it is the guest's form like any other.
  const [form, setForm] = useState<Record<ControlledField, string>>(() =>
    formFrom(invitee)
  );
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  // Seeded from the server, so the confirmation the guest saw when they
  // submitted is the same thing their link shows them a week later.
  const [submitted, setSubmitted] = useState(reserved);

  // The Contact Number field is deliberately uncontrolled. intl-tel-input
  // reformats the input as the guest types ("123456789" becomes "12-345 6789"),
  // and a React `value` fights that rewrite -- the two take turns setting the
  // element and the field intermittently blanks itself. The widget owns the
  // input; submit reads the number back out of it.
  const phoneRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<any>(null);

  // Read straight into the widget below rather than into form state, for the
  // same reason. Pulled out of the prop here so the effect depends on two
  // stable strings instead of an object identity.
  const invitedPhone = invitee?.phone ?? "";
  const invitedCountry = invitee?.country ?? "";

  // The country picker rewrites the input into E.164, which is the only shape
  // PhoneNumber::E164 accepts without also knowing the guest's country -- and
  // this form, unlike the conference one, never asks for a country.
  useEffect(() => {
    const input = phoneRef.current;
    const factory = (window as any).intlTelInput;
    if (!input || typeof factory !== "function") return;

    const iti = factory(input, {
      // The guest's own country when the organisers recorded one, so the
      // picker does not open by claiming a Malaysian number they then have to
      // correct.
      initialCountry: (invitedCountry || "MY").toLowerCase(),
      separateDialCode: true,
      autoPlaceholder: "polite",
      preferredCountries: ["my", "cn", "sg"],
      excludeCountries: ["il", "kp"],
      nationalMode: false,
      loadUtils: () => import(/* @vite-ignore */ ITI_UTILS_URL),
    });
    itiRef.current = iti;

    // Stored E.164, which is exactly what setNumber() reads: it takes the
    // country from the dial code and leaves the national part in the box.
    // Called twice on purpose -- once now, so the number is there even if the
    // CDN never answers, and again when the formatting utils land, which is
    // what turns it into the spaced form the widget displays.
    if (invitedPhone) {
      iti.setNumber(invitedPhone);
      iti.promise?.then(() => iti.setNumber(invitedPhone)).catch(() => {});
    }

    return () => {
      iti.destroy();
      itiRef.current = null;
    };
  }, [invitedCountry, invitedPhone]);

  // Cloudflare Turnstile. The layout loads the API with render=explicit, so the
  // widget is mounted here rather than by the script's own DOM scan, which runs
  // before React has drawn anything. The token lands in a ref: it is read once,
  // at submit, and re-rendering the form on it would tear the widget down.
  const turnstileBoxRef = useRef<HTMLDivElement>(null);
  const turnstileTokenRef = useRef<string>("");
  const turnstileIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only while the form is the thing on screen: the closed and success cards
    // have no container to render into, and polling for one that will never
    // appear would spin for the life of the page.
    if (!turnstileSiteKey || !open || submitted) return;

    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;

    // The API script is async/defer, so it may not have arrived yet.
    const mount = () => {
      if (cancelled) return;

      const api = (window as any).turnstile;
      const box = turnstileBoxRef.current;
      if (!api || !box) {
        // ~10s, then give up. Submit reports the missing token rather than the
        // page retrying forever behind a blocked script.
        if (++attempts > 66) return;
        timer = window.setTimeout(mount, 150);
        return;
      }

      turnstileIdRef.current = api.render(box, {
        sitekey: turnstileSiteKey,
        callback: (t: string) => {
          turnstileTokenRef.current = t;
        },
        // A token is single-use and expires; blank it so submit knows to say so
        // rather than posting a value the server will reject.
        "expired-callback": () => {
          turnstileTokenRef.current = "";
        },
        "error-callback": () => {
          turnstileTokenRef.current = "";
        },
      });
    };

    mount();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      const api = (window as any).turnstile;
      if (api && turnstileIdRef.current !== undefined) {
        api.remove(turnstileIdRef.current);
      }
      turnstileIdRef.current = undefined;
    };
  }, [turnstileSiteKey, open, submitted]);

  const resetTurnstile = () => {
    turnstileTokenRef.current = "";
    const api = (window as any).turnstile;
    if (api && turnstileIdRef.current !== undefined) {
      api.reset(turnstileIdRef.current);
    }
  };

  const clearError = (name: FieldName) =>
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });

  const update = (name: ControlledField, value: string) => {
    setForm((current) => ({ ...current, [name]: value }));
    clearError(name);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    // Turnstile hands over a single-use token; a blocked or expired widget
    // leaves it empty, and posting without one is a guaranteed 403.
    const turnstileToken = turnstileTokenRef.current;
    if (turnstileSiteKey && !turnstileToken) {
      toast.error(
        "Please complete the verification check below, then submit again."
      );
      return;
    }

    setSubmitting(true);
    setErrors({});

    // The widget owns the Contact Number field, so both values are read back
    // out of it here: getNumber() returns what was typed with the dial code
    // attached, and the country picked there is the only country this form
    // collects -- the User model requires one and the design has no field for
    // it.
    const phone = itiRef.current
      ? itiRef.current.getNumber()
      : (phoneRef.current?.value ?? "");
    const country =
      itiRef.current?.getSelectedCountryData()?.iso2?.toUpperCase() ?? "";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        // The token sits at the top level: RailsCloudflareTurnstile reads
        // params["cf-turnstile-response"], not a nested one.
        body: JSON.stringify({
          user: { ...form, phone, country },
          // Names the row this submission completes. It cannot ride inside
          // `user`, which is already the attributes hash.
          invite: invitee?.identityLabel ?? "",
          "cf-turnstile-response": turnstileToken,
        }),
      });

      const result = await response.json();

      // result.reserved marks a submission that had already been counted --
      // a second click, or a tab submitted twice. The seat is booked either
      // way, so say so rather than reporting an error for work that is done.
      if (response.ok || result.reserved) {
        // No toast: the success card that replaces the form says the same thing,
        // and says it in the two languages the design uses.
        setSubmitted(true);
      } else {
        const received: Record<string, string> = { ...(result.errors ?? {}) };
        // Country is collected by the contact number's country picker, so its
        // error belongs on that field rather than against nothing at all.
        if (received.country) {
          received.phone ??= "Please choose the country for your contact number.";
          delete received.country;
        }
        setErrors(received);
        toast.error(result.error ?? "Something went wrong. Please try again.");
        // The token was spent on this attempt, so the retry needs a fresh one.
        resetTurnstile();
      }
    } catch {
      toast.error("We could not reach the server. Please try again.");
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  const panel = () => {
    if (submitted) return <StatusCard copy={SUCCESS_COPY} />;
    if (!open) return <StatusCard copy={CLOSED_COPY} />;

    return (
      <form onSubmit={handleSubmit} noValidate={false} className="w-full">
        <div className="flex flex-col gap-4 md:gap-[18px] xl:gap-[30px]">
          {TEXT_FIELDS.map((field) => (
            <div key={field.name} className="flex flex-col gap-1">
              <Label htmlFor={field.name}>{field.label}</Label>
              <input
                id={field.name}
                name={field.name}
                type={field.type}
                autoComplete={field.autoComplete}
                required
                value={form[field.name]}
                onChange={(event) => update(field.name, event.target.value)}
                className="gala-field"
              />
              <FieldError message={errors[field.name]} />
            </div>
          ))}

          <div className="flex flex-col gap-1">
            <Label htmlFor="phone">Contact number / 联系电话</Label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              required
              ref={phoneRef}
              onChange={() => clearError("phone")}
              className="gala-field"
            />
            <FieldError message={errors.phone} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="main_course_id" id="main_course_label">
              Main course / 主菜
            </Label>
            <MainCourseSelect
              courses={mainCourses}
              selectedId={form.main_course_id}
              onSelect={(id) => update("main_course_id", id)}
            />
            <FieldError message={errors.main_course_id} />
          </div>
        </div>

        {turnstileSiteKey && (
          <div className="mt-8 flex justify-center">
            <div ref={turnstileBoxRef} />
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className={`gala-submit flex h-[58px] w-full items-center justify-center rounded-lg border-2 border-white px-[25px] py-[13px] text-xl font-semibold leading-6 text-white shadow-lg ${
            turnstileSiteKey ? "mt-6" : "mt-10"
          }`}
        >
          {submitting ? "SUBMITTING…" : "SUBMIT"}
        </button>
      </form>
    );
  };

  return (
    <main className="gala-canvas relative min-h-screen w-full overflow-x-clip font-gala">
      <div
        className="gala-flower pointer-events-none absolute left-0 top-0"
        aria-hidden="true"
      />
      <div
        className="gala-flower gala-flower--rotated pointer-events-none absolute bottom-0 right-0"
        aria-hidden="true"
      />

      {/* The two arrangements the designs call for: stacked for phone and
          tablet, side by side for desktop. The split turns on at xl rather than
          lg because it needs roughly 1100px before the invite and the 450px
          form stop crowding each other -- an iPad in portrait is narrower than
          that and takes the stacked layout, which is what its frame shows.
          Stacked, both columns are the same 450px the form is drawn at, which
          is what the phone and tablet frames share: 362px inside a 402px phone,
          and 450px once there is room for it. */}
      <div className="relative mx-auto flex w-full max-w-[1600px] flex-col items-center gap-7 px-5 py-16 xl:min-h-screen xl:flex-row xl:justify-between xl:gap-12 xl:px-16 xl:py-20">
        {/* On the split layout the invite takes the space the 450px form column
            leaves, up to the 800px it is drawn at in the desktop frame. flex-1
            with min-w-0 rather than a width: the image's intrinsic size is
            1600px, which would otherwise set the column's minimum. */}
        <div className="flex w-full max-w-[450px] flex-col items-center gap-5 xl:max-w-[800px] xl:flex-1 xl:min-w-0 xl:gap-14">
          {/* The airline's mark sits above the lockup and is centred on it.
              Stacked it is drawn at a steady ~132px whatever the column does;
              beside the form it is a share of the column, so the mark and the
              lockup read as one piece of artwork as that column grows. */}
          <img
            src={logo}
            alt="Malaysia Airlines"
            className="h-auto w-[132px] xl:w-[32%]"
          />

          <picture className="w-full">
            {/* Switched at the width the layout splits, so "desktop" in the
                admin means the wide lockup drawn to sit beside the form, and
                "mobile" the title-only one drawn to stack above it. */}
            <source srcSet={invite.desktop} media="(min-width: 1280px)" />
            <img
              src={invite.mobile}
              alt={invite.alt}
              className="h-auto w-full"
            />
          </picture>
        </div>

        <div className="w-full max-w-[450px] xl:w-[450px] xl:shrink-0">
          {panel()}
        </div>
      </div>

      <Toaster position="top-center" />
    </main>
  );
};

export default Registration;
